import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { PageHeader } from "@/components/PageHeader";
import { VehicleStatusBadge } from "@/components/StatusBadge";
import { Plus, X, Search, Upload } from "lucide-react";
import { ImportModal } from "./Companies";

export default function Fleet() {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [filter, setFilter] = useState("");

  const load = () => api.get("/vehicles").then((r) => setVehicles(r.data));

  useEffect(() => { load(); }, []);

  const canCreate = user.role === "rentacar_admin" || user.role === "superadmin";
  const filtered = vehicles.filter((v) =>
    `${v.plate} ${v.brand} ${v.model}`.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div data-testid="fleet-page">
      <PageHeader
        tag="Flota"
        title="Vehículos"
        description="Gestiona tu flota: información técnica, documentación y equipamiento."
        action={
          canCreate && (
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowImport(true)}
                className="h-touch px-4 border-2 border-black text-sm font-bold uppercase tracking-wider hover:bg-black hover:text-white transition-colors flex items-center gap-2"
                data-testid="import-vehicles-btn"
              >
                <Upload size={16} strokeWidth={2.5} /> Importar Excel
              </button>
              <button
                onClick={() => setShowForm(true)}
                className="h-touch px-6 bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-blue-600 active:scale-[0.98] transition-colors flex items-center gap-2"
                data-testid="new-vehicle-btn"
              >
                <Plus size={18} strokeWidth={2.5} /> Nuevo Vehículo
              </button>
            </div>
          )
        }
      />

      {/* Search */}
      <div className="mb-6 relative">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Buscar por patente, marca o modelo..."
          className="w-full h-touch pl-12 pr-4 border border-gray-300 focus:outline-none focus:border-black font-mono text-sm"
          data-testid="fleet-search"
        />
      </div>

      {/* Table */}
      <div className="border border-gray-200 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <Th>Patente</Th>
              <Th>Marca / Modelo</Th>
              <Th>Año</Th>
              <Th>Km</Th>
              <Th>Estado</Th>
              <Th></Th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((v) => (
              <tr key={v.id} className="border-b border-gray-200 hover:bg-gray-50" data-testid={`vehicle-row-${v.plate}`}>
                <td className="px-4 py-4 font-mono font-bold text-black">{v.plate}</td>
                <td className="px-4 py-4 text-sm">{v.brand} {v.model}</td>
                <td className="px-4 py-4 text-sm">{v.year}</td>
                <td className="px-4 py-4 text-sm font-mono">{(v.mileage || 0).toLocaleString("es-CL")}</td>
                <td className="px-4 py-4"><VehicleStatusBadge status={v.status} /></td>
                <td className="px-4 py-4 text-right">
                  <Link
                    to={`/flota/${v.id}`}
                    className="text-xs font-bold uppercase tracking-wider text-blue-600 underline"
                    data-testid={`vehicle-detail-${v.plate}`}
                  >
                    Ver detalle
                  </Link>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-sm text-gray-500">
                  Sin vehículos registrados aún.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <NewVehicleModal
          onClose={() => setShowForm(false)}
          onCreated={() => { setShowForm(false); load(); }}
        />
      )}

      {showImport && (
        <ImportModal
          type="vehicles"
          onClose={() => setShowImport(false)}
          onDone={() => { setShowImport(false); load(); }}
        />
      )}
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-700">
      {children}
    </th>
  );
}

function NewVehicleModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    plate: "",
    brand: "",
    model: "",
    year: new Date().getFullYear(),
    vin: "",
    mileage: 0,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/vehicles", {
        ...form,
        plate: form.plate.toUpperCase(),
        year: parseInt(form.year, 10),
        mileage: parseInt(form.mileage, 10),
        equipment: [],
      });
      onCreated();
    } catch (e) {
      setError(e.response?.data?.detail || "Error al crear");
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center p-0 md:p-6" data-testid="new-vehicle-modal">
      <div className="bg-white border-t-4 md:border-4 border-black w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-gray-200 p-6">
          <div>
            <p className="veta-tag">Registro</p>
            <h2 className="font-display text-3xl tracking-tighter">Nuevo Vehículo</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100" data-testid="close-vehicle-modal">
            <X size={24} strokeWidth={2.5} />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Patente" required value={form.plate} onChange={update("plate")} mono testId="field-plate" />
            <Field label="Año" type="number" required value={form.year} onChange={update("year")} testId="field-year" />
            <Field label="Marca" required value={form.brand} onChange={update("brand")} testId="field-brand" />
            <Field label="Modelo" required value={form.model} onChange={update("model")} testId="field-model" />
            <Field label="VIN" value={form.vin} onChange={update("vin")} mono testId="field-vin" />
            <Field label="Kilometraje" type="number" value={form.mileage} onChange={update("mileage")} testId="field-mileage" />
          </div>
          {error && <div className="status-danger px-4 py-3 text-sm">{error}</div>}
          <button
            type="submit"
            disabled={loading}
            className="w-full h-touch bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-blue-600 active:scale-[0.98] transition-colors disabled:opacity-60"
            data-testid="save-vehicle-btn"
          >
            {loading ? "Guardando..." : "Guardar Vehículo"}
          </button>
        </form>
      </div>
    </div>
  );
}

export function Field({ label, value, onChange, type = "text", required, mono, testId, placeholder }) {
  return (
    <div>
      <label className="veta-tag block mb-2">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className={`w-full h-touch px-4 border border-gray-300 focus:outline-none focus:border-black ${mono ? "font-mono" : ""}`}
        data-testid={testId}
      />
    </div>
  );
}
